import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from 'components/ui/button';
import { AuthModal } from 'components/auth/AuthModal';

interface TherapistInfo {
  therapistId: string;
  firstName: string;
  lastName: string;
  specialties?: string[];
  profilePicUrl?: string;
}

interface ReferralData {
  therapistInfo: TherapistInfo | null;
  patientData?: {
    firstName?: string;
    lastName?: string;
    email?: string;
    phone?: string;
  };
  referralCode?: string;
  referralSlug?: string;
}

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';

export const ReferralPage = () => {
  const { slug } = useParams<{ slug: string }>();
  const [searchParams] = useSearchParams();
  const code = searchParams.get('code');
  const { t } = useTranslation();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [referralData, setReferralData] = useState<ReferralData | null>(null);
  const [showAuth, setShowAuth] = useState(false);

  useEffect(() => {
    if (slug) {
      resolveSlug(slug);
    } else if (code) {
      verifyCode(code);
    } else {
      setError('Lien de référence invalide');
      setLoading(false);
    }
  }, [slug, code]);

  const resolveSlug = async (slug: string) => {
    try {
      const response = await fetch(`${API_URL}/api/referral/resolve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ slug }),
      });

      if (!response.ok) {
        throw new Error('Lien de référence non trouvé');
      }

      const data = await response.json();
      setReferralData({
        therapistInfo: data,
        referralSlug: slug,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const verifyCode = async (code: string) => {
    try {
      const response = await fetch(`${API_URL}/api/referral/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code }),
      });

      if (!response.ok) {
        throw new Error('Code de référence invalide');
      }

      const data = await response.json();
      if (!data.isValid) {
        setError(data.message || 'Code de référence invalide ou expiré');
        return;
      }

      setReferralData({
        therapistInfo: data.therapistId ? {
          therapistId: data.therapistId,
          firstName: data.therapistName?.replace('Dr. ', '').split(' ')[0] || '',
          lastName: data.therapistName?.replace('Dr. ', '').split(' ').slice(1).join(' ') || '',
        } : null,
        patientData: data.patientData,
        referralCode: code,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#e0f7fa] to-white flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[#00c4cc] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#e0f7fa] to-white flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 mx-auto mb-4 bg-red-100 rounded-full flex items-center justify-center">
            <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">Lien invalide</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <Button
            onClick={() => window.location.href = '/'}
            className="text-white"
            style={{ backgroundColor: '#00c4cc' }}
          >
            Retour à l'accueil
          </Button>
        </div>
      </div>
    );
  }

  const therapist = referralData?.therapistInfo;

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#e0f7fa] to-white flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full">
        {/* Logo */}
        <div className="text-center mb-6">
          <h1 className="text-2xl font-bold" style={{ color: '#00c4cc' }}>Yapadra</h1>
          <p className="text-sm text-gray-500 mt-1">Thérapie vocale en ligne</p>
        </div>

        {/* Therapist Card */}
        {therapist && (
          <div className="bg-gray-50 rounded-xl p-6 mb-6">
            <div className="flex items-center gap-4">
              {therapist.profilePicUrl ? (
                <img
                  src={therapist.profilePicUrl}
                  alt={`Dr. ${therapist.firstName} ${therapist.lastName}`}
                  className="w-16 h-16 rounded-full object-cover border-2 border-[#00c4cc]"
                />
              ) : (
                <div className="w-16 h-16 rounded-full bg-[#00c4cc] flex items-center justify-center text-white text-xl font-bold">
                  {therapist.firstName[0]}{therapist.lastName[0]}
                </div>
              )}
              <div>
                <h2 className="text-lg font-semibold text-gray-900">
                  Dr. {therapist.firstName} {therapist.lastName}
                </h2>
                {therapist.specialties && therapist.specialties.length > 0 && (
                  <p className="text-sm text-gray-500 mt-1">
                    {therapist.specialties.slice(0, 3).join(', ')}
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Welcome message */}
        <div className="text-center mb-6">
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {therapist
              ? `Dr. ${therapist.firstName} ${therapist.lastName} vous invite sur Yapadra`
              : 'Vous avez été invité sur Yapadra'}
          </h3>
          <p className="text-sm text-gray-600">
            Inscrivez-vous pour commencer votre accompagnement thérapeutique en ligne.
          </p>
        </div>

        {/* Pre-filled data notice */}
        {referralData?.patientData && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-6">
            <p className="text-sm text-blue-700">
              Vos informations ont été pré-remplies par votre thérapeute pour faciliter votre inscription.
            </p>
          </div>
        )}

        {/* CTA */}
        <Button
          onClick={() => setShowAuth(true)}
          className="w-full text-white text-lg py-6"
          style={{ backgroundColor: '#00c4cc' }}
        >
          {therapist
            ? `S'inscrire avec Dr. ${therapist.lastName}`
            : "S'inscrire"}
        </Button>

        <p className="text-xs text-gray-400 text-center mt-4">
          Gratuit et confidentiel. Vos données sont protégées.
        </p>
      </div>

      {/* Auth Modal */}
      <AuthModal
        isOpen={showAuth}
        onClose={() => setShowAuth(false)}
        referralCode={referralData?.referralCode}
        referralSlug={referralData?.referralSlug}
        prefillData={referralData?.patientData}
        referredByTherapistId={therapist?.therapistId}
      />
    </div>
  );
};
